/**
 * Web-side mirror of `shared/src/util/utma.ts`. Web does NOT import from
 * `@hearth/shared` (locked since 5.1). Behavior must match the shared
 * implementation; matching tests at `web/src/lib/util/utma.test.ts` are
 * the contract.
 *
 * See `shared/src/util/utma.ts` for state-statute citations.
 */

const UTMA_AGE_OF_MAJORITY_BY_STATE: Record<string, number> = {
  AK: 21,
  AL: 21,
  KY: 21,
  LA: 21,
  ME: 21,
  MS: 21,
  NV: 21,
  NY: 21,
  TN: 21,
  WY: 21,
};

const DEFAULT_UTMA_AGE_OF_MAJORITY = 18;

export function utmaAgeOfMajority(state_code: string): number {
  return UTMA_AGE_OF_MAJORITY_BY_STATE[state_code] ?? DEFAULT_UTMA_AGE_OF_MAJORITY;
}

export interface UtmaMilestone {
  age: number;
  action: string;
  date: string;
  is_age_of_majority: boolean;
}

interface MemberLike {
  date_of_birth?: string;
}

function addYearsIso(dob: string, years: number): string {
  const [yStr, mStr, dStr] = dob.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const targetYear = y + years;
  const isLeap =
    (targetYear % 4 === 0 && targetYear % 100 !== 0) || targetYear % 400 === 0;
  const clampedDay = m === 2 && d === 29 && !isLeap ? 28 : d;
  const mm = String(m).padStart(2, "0");
  const dd = String(clampedDay).padStart(2, "0");
  return `${targetYear}-${mm}-${dd}`;
}

export function utmaMilestones(
  member: MemberLike,
  state_code: string,
): UtmaMilestone[] {
  if (!member.date_of_birth) return [];
  const dob = member.date_of_birth;
  const ageOfMajority = utmaAgeOfMajority(state_code);
  const milestones: UtmaMilestone[] = [
    {
      age: 14,
      action: "Custodial Roth eligibility window opens (with earned income)",
      date: addYearsIso(dob, 14),
      is_age_of_majority: false,
    },
    {
      age: 17,
      action:
        "Pre-turnover reallocation review — rotate concentrated positions to fixed income",
      date: addYearsIso(dob, 17),
      is_age_of_majority: false,
    },
  ];
  if (ageOfMajority !== 18) {
    milestones.push({
      age: 18,
      action:
        "Federal age of majority — UTMA custodial control persists until state age",
      date: addYearsIso(dob, 18),
      is_age_of_majority: false,
    });
  }
  milestones.push({
    age: ageOfMajority,
    action: `Age of majority (${state_code}) — account control transfers to beneficiary`,
    date: addYearsIso(dob, ageOfMajority),
    is_age_of_majority: true,
  });
  return milestones;
}

export function nextUpcomingMilestone(
  member: MemberLike,
  state_code: string,
  asOf?: Date,
): UtmaMilestone | null {
  if (!member.date_of_birth) return null;
  const ref = (asOf ?? new Date()).toISOString().slice(0, 10);
  const ms = utmaMilestones(member, state_code);
  for (const m of ms) {
    if (ref < m.date) return m;
  }
  return null;
}

export function isApproachingMilestone(
  member: MemberLike,
  state_code: string,
  windowMonths = 12,
  asOf?: Date,
): boolean {
  const next = nextUpcomingMilestone(member, state_code, asOf);
  if (!next) return false;
  const ref = asOf ?? new Date();
  const diffMs = new Date(next.date).getTime() - ref.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.4375);
  return diffMonths >= 0 && diffMonths <= windowMonths;
}
